import * as React from "react";
import { Input } from "./input";
import {
  customElementAttributeMappers,
  setCustomElementMetadata,
} from "@/lib/customElements";
import { NumberInputProps } from "@/models/input";

export const CurrencyInput = React.forwardRef<
  HTMLInputElement,
  NumberInputProps
>(({ value, onChange, ...props }, ref) => {
  const [displayValue, setDisplayValue] = React.useState("");

  const formatValue = React.useCallback(() => {
    const formatted = new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value !== null ? value : 0);
    setDisplayValue(formatted);
  }, [value]);

  React.useEffect(() => {
    formatValue();
  }, [value, formatValue]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value.replace(/[^0-9\\.]/g, "");
    const numValue = rawValue ? parseFloat(rawValue) : null;
    onChange(numValue);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowUp" || e.key === "ArrowDown") {
      e.preventDefault();
      const step = e.shiftKey ? 10 : 1;
      const currentValue = value !== null ? value : 0;
      const newValue = e.key === "ArrowUp" ? currentValue + step : currentValue - step;
      onChange(newValue);
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    handleChange(e);
    formatValue();
  };

  return (
    <Input
      type="text"
      inputMode="decimal"
      ref={ref}
      value={displayValue}
      onKeyDown={handleKeyDown}
      onChange={(e) => setDisplayValue(e.target.value)}
      onBlur={handleBlur}
      {...props}
    />
  );
});

CurrencyInput.displayName = "CurrencyInput";

setCustomElementMetadata({
  component: CurrencyInput,
  tagName: "raes-currency-input",
  observedAttributes: {
    name: customElementAttributeMappers.string,
    placeholder: customElementAttributeMappers.string,
    required: customElementAttributeMappers.boolean,
    disabled: customElementAttributeMappers.boolean,
    value: (value: string) => ({
      ...customElementAttributeMappers.number(value),
      prop: "defaultValue",
    }),
  },
});
